import "../css/header.scss";
import React, { useContext, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { FiShoppingBag } from "react-icons/fi";
import { CartContext } from "./CartContext";
import productData from "../Datas/productData";
import brandConfig from "../config";

function Header() {
  const { cartItems, wishlist, toggleWishlist } = useContext(CartContext);
  const [menuOpen, setMenuOpen] = useState(false);
  const [savedOpen, setSavedOpen] = useState(false);
  const [query, setQuery] = useState("");
  const savedPanel = useRef(null);
  const savedTrigger = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!savedOpen) return undefined;
    const trigger = savedTrigger.current;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const close = (event) => {
      if (event.key === "Escape") setSavedOpen(false);
      if (event.key === "Tab" && savedPanel.current) {
        const focusable = [
          ...savedPanel.current.querySelectorAll(
            "button:not([disabled]), a[href]"
          ),
        ];
        if (!focusable.length) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", close);
    savedPanel.current?.focus();
    return () => {
      document.removeEventListener("keydown", close);
      document.body.style.overflow = previousOverflow;
      trigger?.focus();
    };
  }, [savedOpen]);

  const submitSearch = (event) => {
    event.preventDefault();
    navigate(
      `/shop${query.trim() ? `?q=${encodeURIComponent(query.trim())}` : ""}`
    );
    setMenuOpen(false);
  };

  const savedGames = wishlist
    .map((id) => productData.find((game) => game.id === id))
    .filter(Boolean);

  return (
    <>
      <header className="site-header">
        <div className="site-header__inner">
          <Link
            className="brand"
            to="/"
            aria-label={`${brandConfig.brandName} home`}
          >
            <span className="brand__mark" aria-hidden="true">
              {brandConfig.brandInitial}
            </span>
            <span>{brandConfig.brandName}</span>
          </Link>
          <button
            className="menu-toggle"
            type="button"
            aria-expanded={menuOpen}
            aria-controls="site-nav"
            onClick={() => setMenuOpen((open) => !open)}
          >
            <span className="sr-only">
              {menuOpen ? "Close menu" : "Open menu"}
            </span>
            {menuOpen ? "×" : "☰"}
          </button>
          <div
            id="site-nav"
            className={`site-header__nav ${menuOpen ? "is-open" : ""}`}
          >
            <nav aria-label="Primary navigation">
              {brandConfig.navigation.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  onClick={() => setMenuOpen(false)}
                >
                  {item.label}
                </NavLink>
              ))}
            </nav>
            <form
              className="header-search"
              role="search"
              onSubmit={submitSearch}
            >
              <label className="sr-only" htmlFor="site-search">
                Search games
              </label>
              <input
                id="site-search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Search games"
              />
              <button type="submit" aria-label="Submit search">
                ⌕
              </button>
            </form>
            <div className="header-actions">
              <button
                className="header-counter"
                ref={savedTrigger}
                type="button"
                onClick={() => setSavedOpen(true)}
                aria-label={`${wishlist.length} saved games`}
              >
                ♡ <span>{wishlist.length}</span>
              </button>
              <Link
                className="header-counter"
                to="/cartpage"
                aria-label={`${cartItems.length} games in cart`}
                onClick={() => setMenuOpen(false)}
              >
                <FiShoppingBag aria-hidden="true" />
                <span>{cartItems.length}</span>
              </Link>
            </div>
          </div>
        </div>
      </header>
      {savedOpen &&
        createPortal(
          <div
            className="drawer-backdrop"
            onMouseDown={() => setSavedOpen(false)}
          >
            <aside
              className="saved-drawer"
              role="dialog"
              aria-modal="true"
              aria-label="Saved games"
              tabIndex="-1"
              ref={savedPanel}
              onMouseDown={(event) => event.stopPropagation()}
            >
              <div className="drawer-heading">
                <div>
                  <p className="eyebrow">Your list</p>
                  <h2>Saved games</h2>
                </div>
                <button
                  className="icon-button"
                  onClick={() => setSavedOpen(false)}
                  aria-label="Close saved games"
                >
                  ×
                </button>
              </div>
              {savedGames.length ? (
                savedGames.map((game) => (
                  <div className="saved-item" key={game.id}>
                    <img src={game.coverImage} alt="" />
                    <div>
                      <strong>{game.name}</strong>
                      <span>{game.category}</span>
                    </div>
                    <Link
                      to={`/product/${game.id}`}
                      onClick={() => setSavedOpen(false)}
                    >
                      View
                    </Link>
                    <button
                      className="icon-button"
                      onClick={() => toggleWishlist(game.id)}
                      aria-label={`Remove ${game.name}`}
                    >
                      ×
                    </button>
                  </div>
                ))
              ) : (
                <div className="empty-mini">
                  <span>♡</span>
                  <p>No saved games yet.</p>
                  <Link to="/shop" onClick={() => setSavedOpen(false)}>
                    Browse the arcade
                  </Link>
                </div>
              )}
            </aside>
          </div>,
          document.body
        )}
    </>
  );
}

export default Header;
